import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Users, BarChart3, Clock, Star, Check } from "lucide-react";
import heroImage from "@/assets/hero-salon.jpg";
import { Button } from "@/components/ui/button";
import { testimonials, pricingPlans } from "@/data/dummy";
import { SchedoraLogo } from "@/components/schedora/SchedoraLogo";

const features = [
  { icon: Calendar, title: "Smart Scheduling", desc: "Automated booking with real-time availability and conflict prevention." },
  { icon: Users, title: "Client Management", desc: "Complete client profiles, history tracking, and personalized notes." },
  { icon: BarChart3, title: "Business Insights", desc: "Revenue analytics, booking trends, and performance dashboards." },
  { icon: Clock, title: "Staff Coordination", desc: "Manage schedules, availability, and workload across your team." },
];

const steps = [
  { num: "01", title: "Set Up Your Business", desc: "Add your services, team, and working hours in minutes." },
  { num: "02", title: "Share Your Booking Link", desc: "Clients book online 24/7 — no calls, no back-and-forth." },
  { num: "03", title: "Manage & Grow", desc: "Track appointments, revenue, and clients from one dashboard." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md">
        <div className="container flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <SchedoraLogo textClassName="text-xl" />
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#how" className="hover:text-foreground transition-colors">How It Works</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" size="sm">Log in</Button></Link>
            <Link to="/signup"><Button size="sm">Start Free</Button></Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero opacity-50" />
        <div className="container relative py-24 md:py-36 text-center">
          <div className="mx-auto max-w-3xl animate-fade-in">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary mb-6">
              <SchedoraLogo iconOnly iconClassName="h-3 w-3" /> Built for Beauty & Wellness
            </span>
            <h1 className="text-4xl md:text-6xl font-heading font-bold tracking-tight text-foreground leading-tight">
              Run your beauty business with <span className="text-primary">clarity</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Booking, staff, clients, and calendar — all in one place. The premium platform trusted by salons, spas, and beauty studios.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/signup"><Button size="lg" className="px-8">Start Free <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/signup?demo=true"><Button size="lg" variant="outline" className="px-8">Book a Demo</Button></Link>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">No credit card required · Free 14-day trial</p>
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="container -mt-8 mb-16">
        <div className="mx-auto max-w-5xl rounded-2xl border bg-card shadow-elevated p-6 md:p-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Today's Bookings", value: "12" },
              { label: "Revenue", value: "$1,850" },
              { label: "New Clients", value: "3" },
              { label: "Completion Rate", value: "94%" },
            ].map(s => (
              <div key={s.label} className="rounded-xl bg-secondary/50 p-4 text-center">
                <p className="text-2xl font-heading font-bold text-foreground">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-xl overflow-hidden">
            <img src={heroImage} alt="Modern salon interior" width={1280} height={720} className="w-full h-auto rounded-xl" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="container py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground">Everything you need to thrive</h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">Powerful tools designed specifically for beauty and wellness businesses.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map(f => (
            <div key={f.title} className="rounded-xl border bg-card p-6 shadow-soft hover:shadow-card transition-shadow">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 mb-4">
                <f.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-card-foreground mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section id="how" className="bg-secondary/30 py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground">How it works</h2>
            <p className="mt-3 text-muted-foreground">Get started in three simple steps.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map(s => (
              <div key={s.num} className="text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground font-heading text-lg font-bold mb-4">{s.num}</div>
                <h3 className="font-semibold text-foreground mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="container py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground">Loved by beauty professionals</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map(t => (
            <div key={t.name} className="rounded-xl border bg-card p-6 shadow-soft">
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-gold text-gold" />)}
              </div>
              <p className="text-sm text-card-foreground italic">"{t.text}"</p>
              <div className="mt-4">
                <p className="text-sm font-semibold text-foreground">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-secondary/30 py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground">Simple, transparent pricing</h2>
            <p className="mt-3 text-muted-foreground">Start free. Upgrade as you grow.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {pricingPlans.map(p => (
              <div key={p.name} className={`rounded-xl border bg-card p-6 shadow-soft relative ${p.popular ? "ring-2 ring-primary border-primary" : ""}`}>
                {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground">Most Popular</span>}
                <h3 className="font-heading text-lg font-semibold text-foreground">{p.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-heading font-bold text-foreground">${p.price}</span>
                  <span className="text-sm text-muted-foreground">/{p.period}</span>
                </div>
                <ul className="mt-5 space-y-2">
                  {p.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-success flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="block mt-6">
                  <Button className="w-full" variant={p.popular ? "default" : "outline"}>{p.popular ? "Start Free Trial" : "Get Started"}</Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container py-20 text-center">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground">Ready to elevate your business?</h2>
          <p className="mt-4 text-muted-foreground">Join hundreds of beauty professionals using Schedora to simplify their operations.</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup"><Button size="lg" className="px-8">Start Free <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <Link to="/signup?demo=true"><Button size="lg" variant="outline" className="px-8">Book a Demo</Button></Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-card py-10">
        <div className="container flex flex-col md:flex-row items-center justify-between gap-4">
          <SchedoraLogo iconClassName="h-5 w-5" />
          <p className="text-xs text-muted-foreground">© 2026 Schedora. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
